import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, Globe } from "lucide-react";
import { SUPPORTED_LANGUAGES, RTL_LANGUAGES } from "@/i18n";
import "flag-icons/css/flag-icons.min.css";

const LangIcon = ({ lang }) =>
  lang.flag ? (
    <span className={`fi fi-${lang.flag} rounded-[1px] shrink-0`} style={{ width: 16, height: 12 }} aria-hidden="true" data-testid={`language-flag-${lang.code}`} />
  ) : (
    <Globe size={13} className="shrink-0" aria-hidden="true" data-testid={`language-flag-${lang.code}`} />
  );

export default function LanguageSwitcher({ variant = "nav" }) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  // Apply lang + dir to <html> when language changes
  useEffect(() => {
    const apply = () => {
      const lng = i18n.resolvedLanguage || i18n.language || "en";
      document.documentElement.lang = lng;
      document.documentElement.dir = RTL_LANGUAGES.includes(lng) ? "rtl" : "ltr";
    };
    apply();
    i18n.on("languageChanged", apply);
    return () => i18n.off("languageChanged", apply);
  }, [i18n]);

  const current =
    SUPPORTED_LANGUAGES.find((l) => l.code === (i18n.resolvedLanguage || i18n.language)) ||
    SUPPORTED_LANGUAGES[0];

  const onChange = (code) => {
    i18n.changeLanguage(code);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative" data-testid="language-switcher">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] transition-colors duration-300 ${
          variant === "nav"
            ? "text-[var(--bone-2)] hover:text-[var(--pewter)] px-3 py-2"
            : "text-[var(--bone)] hover:text-[var(--pewter)]"
        }`}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid="language-switcher-trigger"
      >
        <LangIcon lang={current} />
        <span>{current.short}</span>
        <ChevronDown
          size={12}
          className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div
          role="listbox"
          data-testid="language-switcher-menu"
          className="absolute right-0 mt-3 min-w-[180px] py-2 z-50"
          style={{
            background: "rgba(10,11,13,0.96)",
            backdropFilter: "blur(20px)",
            border: "1px solid var(--line-2)",
          }}
        >
          {SUPPORTED_LANGUAGES.map((l) => {
            const isActive = l.code === current.code;
            return (
              <button
                key={l.code}
                onClick={() => onChange(l.code)}
                role="option"
                aria-selected={isActive}
                data-testid={`language-option-${l.code}`}
                className={`w-full text-left px-5 py-3 flex items-center justify-between gap-6 transition-colors duration-200 ${
                  isActive
                    ? "text-[var(--pewter)]"
                    : "text-[var(--bone-2)] hover:text-[var(--bone)] hover:bg-[var(--ink-2)]"
                }`}
              >
                <span className="flex items-center gap-3">
                  <LangIcon lang={l} />
                  <span className="font-serif text-[15px]"
                    style={{ fontVariationSettings: '"opsz" 144, "SOFT" 30, "wght" 400' }}>
                    {l.label}
                  </span>
                </span>
                <span className="font-mono text-[10px] tracking-[0.22em] text-[var(--mute-2)]">
                  {l.short}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
